import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function GoogleCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const code = params.get("code");
    if (!code) {
      setError("No authorization code received from Google.");
      return;
    }

    const exchange = async () => {
      try {
        const storedRef = localStorage.getItem("jj_referral_code") || "";
        const { data } = await api.post("/auth/google", {
          code,
          referral_code: storedRef || null
        });

        if (data.token) {
          localStorage.setItem("jjodi_token", data.token);
        }
        setUser(data.user);
        
        // Clear referral code once registered
        localStorage.removeItem("jj_referral_code");
        
        toast.success("Signed in successfully with Google!");
        const isStaff = data.user.role === "admin" || data.user.role === "mitra" || data.user.is_mitra;
        navigate(isStaff ? "/app" : (data.is_new || !data.user.profile_complete ? "/onboarding" : "/app"));
      } catch (err) {
        setError(formatError(err) || "Failed to authenticate with Google.");
        toast.error("Google authentication failed.");
      }
    };

    exchange();
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="absolute inset-0 mandala-bg pointer-events-none opacity-20" />
      <div className="relative w-full max-w-md text-center card-soft p-8">
        <div className="flex justify-center gap-2 mb-6">
          <Sparkles className="w-8 h-8 text-secondary animate-pulse" />
          <span className="font-heading text-3xl text-primary">Hamarsathi</span>
        </div>

        {error ? (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-destructive">Authentication Error</h2>
            <p className="text-muted-foreground text-sm">{error}</p>
            <Button onClick={() => navigate("/login")} className="w-full rounded-full h-11">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sign-in
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <h2 className="text-xl font-medium text-foreground">Verifying Google Account</h2>
            <p className="text-muted-foreground text-sm">Please wait while we log you in...</p>
          </div>
        )}
      </div>
    </div>
  );
}
